import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/datatable/datatable";
import { fetchDashboardData, adminFetchAllApplications } from "../../core/axios/endpoints";
import { RoutesFullPath } from "../../utils/constants";

const AdminDashboardPage = () => {
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [dashboardData, setDashboardData] = useState({
    totalApplications: 0,
    applicationCounts: {
      eia: 0,
      eiaZon: 0,
      isde: 0,
    },
    totalPaidInvoices: 0,
    btwRevenuePaid: 0,
    btwPaidCount: 0,
    btwAssignedToMe: 0,
    btwUnassigned: 0,
    btwStuck: 0,
    partnerInvoicePaidNet: 0,
    partnerInvoiceOpen: 0,
    partnerInvoiceCount: 0,
    partnerCreditNoteCount: 0,
    totalRevenue: 0,
    totalRevenueProjected: 0,
    btwRevenueExpected: 0,
    btwRevenueExpectedCustomer: 0,
    btwRevenueExpectedPartner: 0,
    btwExpectedPartnerCount: 0,
    latestApplications: [],
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();


  const formatNumber = (number) => {
    return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };


  const columns = [
    {
      title: "Naam",
      dataIndex: "naam",
      render: (text, record) => <span>{record?.klantId?.naam}</span>
    },
    {
      title: "Regeling",
      dataIndex: "regling",
      sorter: (a, b) => a.regling.localeCompare(b.regling),
    },
    {
      title: "Datum",
      dataIndex: "createdAt",
      render: (text) => <span>{new Date(text).toLocaleDateString()}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Bedrag",
      dataIndex: "totaal",
      render: (text) => <span>€{formatNumber(text || '0')}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
    },
  ];

  const fetchData = async ({ page, pageSize, resetPageSize }) => {
    setLoading(true);
    try {
      const reglings = ["EIA", "EIA Zon", "ISDE"];
      const limitPerRegling = page * pageSize;
      const responses = await Promise.all(
        reglings.map((regling) =>
          adminFetchAllApplications(1, limitPerRegling, "", regling, "new")
        )
      );

      const merged = responses
        .flatMap((r) => r.docs || [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paged = merged.slice(start, end).map((item, index) => ({ ...item, key: start + index }));

      const totalDataCount = responses.reduce((sum, r) => sum + (r.totalDocs || 0), 0);

      const result = {
        data: paged,
        totalDataCount,
        resetPageSize,
      };
      setData(result);
    } catch (error) {
      console.error("Error fetching applications for dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardsData = async () => {
    try {
      const response = await fetchDashboardData();
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const handleViewClick = (record) => {
    navigate(`${RoutesFullPath.aanvraagDetailsEIAAdmin}/${record?._id}`, {
        state: {admin:true}
    });
  };



  useEffect(() => {
    fetchCardsData();
  }, []);

  const fmt = (n) =>
    (n ?? 0).toLocaleString("nl-NL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const totalAanvragen =
    (dashboardData.totalApplications ?? 0) + (dashboardData.btwPaidCount ?? 0);

  return (
    <div className="page-wrapper">
      <div className="content admin-pages px-6 pt-6 pb-12">
        <header className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Overzicht van omzet, aanvragen en openstaande acties.
          </p>
        </header>

        {/* Hero KPI row */}
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm ring-1 ring-emerald-200">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
              Totaal omzet
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-emerald-900">
              € {fmt(dashboardData.totalRevenue)}
            </p>
            <p className="mt-1 text-[11px] text-emerald-700/70">
              Subsidie + BTW betaald
            </p>
            {dashboardData.btwRevenueExpected ? (
              <p className="mt-1 text-[11px] font-medium text-amber-700">
                + € {fmt(dashboardData.btwRevenueExpected)} verwacht = € {fmt(dashboardData.totalRevenueProjected)} projectie
              </p>
            ) : null}
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Subsidie-omzet
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              € {fmt(dashboardData.totalPaidInvoices)}
            </p>
            <p className="mt-1 text-[11px] text-gray-400">EIA · EIA Zon · ISDE</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              BTW-teruggave omzet
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              € {fmt((dashboardData.btwRevenuePaid ?? 0) + (dashboardData.partnerInvoicePaidNet ?? 0))}
            </p>
            <p className="mt-1 text-[11px] text-gray-400">
              {dashboardData.btwPaidCount ?? 0} aanvragen betaald
            </p>
            {dashboardData.btwRevenueExpected ? (
              <p className="mt-1 text-[11px] font-medium text-amber-700">
                € {fmt(dashboardData.btwRevenueExpected)} verwacht (nog niet betaald)
              </p>
            ) : null}
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Totaal aanvragen
            </p>
            <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
              {totalAanvragen}
            </p>
            <p className="mt-1 text-[11px] text-gray-400">
              {dashboardData.totalApplications ?? 0} subsidie ·{" "}
              {dashboardData.btwPaidCount ?? 0} BTW
            </p>
          </div>
        </div>

        {/* Action row - BTW workflow */}
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Te doen - BTW workflow
          </h2>
          <button
            type="button"
            onClick={() => navigate("/panel/admin/btw-batterij-board")}
            className="text-xs font-medium text-emerald-700 hover:underline"
          >
            Open board →
          </button>
        </div>
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            {
              label: "Toegewezen aan mij",
              value: dashboardData.btwAssignedToMe ?? 0,
              to: "/panel/admin/btw-batterij-board?mine=1",
              dot: "bg-emerald-500",
              hoverRing: "hover:ring-emerald-300",
            },
            {
              label: "Niet toegewezen",
              value: dashboardData.btwUnassigned ?? 0,
              to: "/panel/admin/btw-batterij-board?unassigned=1",
              dot: "bg-amber-500",
              hoverRing: "hover:ring-amber-300",
            },
            {
              label: "Vastgelopen (>7d)",
              value: dashboardData.btwStuck ?? 0,
              to: "/panel/admin/btw-batterij-board?stuck=1",
              dot: "bg-rose-500",
              hoverRing: "hover:ring-rose-300",
            },
          ].map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={() => navigate(card.to)}
              className={`group flex items-center justify-between rounded-xl bg-white p-5 text-left shadow-sm ring-1 ring-gray-200 transition hover:shadow-md ${card.hoverRing}`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${card.dot}`} />
                  <span className="text-sm font-medium text-gray-700">
                    {card.label}
                  </span>
                </div>
                <p
                  className={`mt-2 text-3xl font-semibold tabular-nums ${
                    card.value > 0 ? "text-gray-900" : "text-gray-300"
                  }`}
                >
                  {card.value}
                </p>
              </div>
              <svg
                className="h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.3 4.3a1 1 0 011.4 0l5 5a1 1 0 010 1.4l-5 5a1 1 0 11-1.4-1.4L11.6 10 7.3 5.7a1 1 0 010-1.4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ))}
        </div>

        {/* Breakdowns */}
        <div className="mb-8 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <div className="flex items-baseline justify-between border-b border-gray-100 pb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Subsidie-aanvragen
              </h3>
              <span className="text-xl font-semibold tabular-nums text-gray-900">
                {dashboardData.totalApplications ?? 0}
              </span>
            </div>
            <dl className="mt-3 space-y-2">
              {[
                { label: "EIA", value: dashboardData.applicationCounts?.eia ?? 0 },
                { label: "EIA Zon", value: dashboardData.applicationCounts?.eiaZon ?? 0 },
                { label: "ISDE", value: dashboardData.applicationCounts?.isde ?? 0 },
              ].map((it) => (
                <div
                  key={it.label}
                  className="flex items-center justify-between text-sm"
                >
                  <dt className="text-gray-500">{it.label}</dt>
                  <dd className="font-medium tabular-nums text-gray-900">
                    {it.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
            <div className="flex items-baseline justify-between border-b border-gray-100 pb-3">
              <h3 className="text-sm font-semibold text-gray-900">
                BTW-aanvragen
              </h3>
              <span className="text-xl font-semibold tabular-nums text-gray-900">
                {dashboardData.btwPaidCount ?? 0}
              </span>
            </div>
            <dl className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <dt className="text-gray-500">Omzet betaald</dt>
                <dd className="font-medium tabular-nums text-emerald-700">
                  € {fmt((dashboardData.btwRevenuePaid ?? 0) + (dashboardData.partnerInvoicePaidNet ?? 0))}
                </dd>
              </div>
              {dashboardData.btwRevenueExpected ? (
                <div className="flex items-center justify-between text-sm">
                  <dt className="text-gray-500">Verwacht (nog niet betaald)</dt>
                  <dd className="font-medium tabular-nums text-amber-700">
                    € {fmt(dashboardData.btwRevenueExpected)}
                  </dd>
                </div>
              ) : null}
              {dashboardData.partnerInvoiceOpen ? (
                <div className="flex items-center justify-between text-xs">
                  <dt className="text-gray-400">waarvan al gefactureerd (verstuurd, open)</dt>
                  <dd className="tabular-nums text-gray-500">
                    € {fmt(dashboardData.partnerInvoiceOpen)}
                  </dd>
                </div>
              ) : null}
              {dashboardData.partnerCreditNoteCount ? (
                <div className="flex items-center justify-between text-xs">
                  <dt className="text-gray-400">Creditnota&apos;s</dt>
                  <dd className="text-gray-500">
                    {dashboardData.partnerCreditNoteCount}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>

        {/* Recent subsidie applications */}
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Recente subsidie-aanvragen
              </h2>
              <p className="text-[11px] text-gray-500">
                EIA · EIA Zon · ISDE - nog niet afgerond
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/panel/admin/eia-overzicht")}
              className="text-xs font-medium text-emerald-700 hover:underline"
            >
              Alle subsidie-aanvragen →
            </button>
          </div>
          <Table
            columns={columns}
            response={data}
            fetchData={fetchData}
            onRowClick={handleViewClick}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
